import React, { useState, useEffect } from 'react';
import { fmtFull } from '../utils/formatters';
import { Campaign } from '../data';
import { getCampaigns, getReachCampaigns, getCampaignImpressionsByDisplayUnit, getAllScreens, getScreenCreatives, ScreenSummary, EntityTotals, CreativeSummary } from '../adapters/campaigns';
import { NetworkMap, NetworkMapHandle } from './NetworkMap';
import { useScreenEdits } from './useScreenEdits';
import { useMapPrefs } from './useMapPrefs';
import { StaticNetworkMap } from './StaticNetworkMap';
import html2canvas from 'html2canvas';

const BTN: React.CSSProperties = {
  padding: '6px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
  background: 'var(--bg-surface)', color: 'var(--fg-strong)', border: '1px solid var(--border-soft)',
};
const INPUT: React.CSSProperties = {
  padding: '6px 10px', fontSize: 12, borderRadius: 8, border: '1px solid var(--border-soft)',
  background: 'var(--bg-surface)', color: 'var(--fg-strong)', minWidth: 0,
};

function StatWidget({ label, value, sub, dot }: { label: string; value: string | number; sub?: string; dot?: string }) {
  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: dot, boxShadow: `0 0 0 3px ${dot}33` }} />}
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-muted)' }}>{label}</span>
      </div>
      <div className="num" style={{ fontSize: 26, fontWeight: 700, color: 'var(--fg-strong)', lineHeight: 1, marginBottom: 4 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>{sub}</div>}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 10 }}>{children}</div>
  );
}

function VideoThumb({ src }: { src: string }) {
  return (
    <video src={src + '#t=0.001'} preload="metadata" muted playsInline
      style={{ width: 56, height: 100, borderRadius: 6, objectFit: 'cover', display: 'block', flexShrink: 0 }} />
  );
}

function ScreenDetailPage({ screen, campaigns, campaignKey, startDate, onBack }: {
  screen: ScreenSummary;
  campaigns: Campaign[];
  campaignKey: string;
  startDate: string;
  onBack: () => void;
}) {
  const [creatives, setCreatives] = useState<CreativeSummary[]>([]);
  const [creativesLoading, setCreativesLoading] = useState(false);
  const [campaignRows, setCampaignRows] = useState<{ campaign: Campaign; plays: number; impressions: number }[]>([]);
  const [campaignRowsLoading, setCampaignRowsLoading] = useState(false);

  useEffect(() => {
    if (!campaignKey || !startDate) return;
    setCreativesLoading(true);
    getScreenCreatives(screen.id, campaignKey, startDate)
      .then(setCreatives)
      .catch(() => setCreatives([]))
      .finally(() => setCreativesLoading(false));

    setCampaignRowsLoading(true);
    Promise.all(
      campaigns.map(c => getCampaignImpressionsByDisplayUnit(c.id, c.startDate)
        .then(totals => ({ campaign: c, counts: totals[screen.id] }))
        .catch(() => ({ campaign: c, counts: undefined }))
      )
    ).then(results => {
      setCampaignRows(
        results
          .filter((r): r is { campaign: Campaign; counts: EntityTotals[string] } => !!r.counts && (r.counts.plays > 0 || r.counts.impressions > 0))
          .map(r => ({ campaign: r.campaign, plays: r.counts.plays, impressions: r.counts.impressions }))
      );
    }).catch(() => {}).finally(() => setCampaignRowsLoading(false));
  }, [screen.id, campaignKey, startDate]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={onBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8,
            border: '1px solid var(--border-soft)', background: 'transparent',
            color: 'var(--fg-muted)', fontSize: 12, fontWeight: 700, cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--fg-strong)', letterSpacing: '-0.01em', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {screen.venue || screen.name}
        </div>
      </div>

      {/* Details */}
      <div className="card" style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
        <SectionLabel>Details</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
          {[
            { label: 'Screen ID', value: screen.id },
            { label: 'Venue', value: screen.venue || screen.name },
            { label: 'Coordinates', value: `${screen.lat.toFixed(5)}, ${screen.lon.toFixed(5)}` },
          ].map(row => (
            <div key={row.label}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--fg-muted)', marginBottom: 3 }}>{row.label}</div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-strong)', wordBreak: 'break-all', lineHeight: 1.4 }}>{row.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Campaigns */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionLabel>Campaigns</SectionLabel>
            {campaignRowsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[1, 2].map(i => <div key={i} className="skeleton" style={{ height: 36, borderRadius: 6 }} />)}
              </div>
            ) : campaignRows.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--fg-faint)', padding: '8px 0' }}>No campaign data for this screen</div>
            ) : (
              <div style={{ maxHeight: 300, overflowY: 'auto' }}>
                {campaignRows.map((row, i) => (
                  <div key={row.campaign.id}
                    onClick={() => {
                      window.history.pushState(null, '', `/campaigns/${row.campaign.id}`);
                      window.dispatchEvent(new PopStateEvent('popstate'));
                    }}
                    style={{
                      display: 'grid', gridTemplateColumns: '1fr 90px 110px',
                      alignItems: 'center', gap: 12,
                      borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none',
                      cursor: 'pointer', transition: 'background 0.1s',
                      margin: '0 -20px', padding: '10px 20px',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.campaign.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>{row.campaign.flight}</div>
                    </div>
                    <div className="num" style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Plays</div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-default)' }}>{fmtFull(row.plays)}</span>
                    </div>
                    <div className="num" style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Impressions</div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-green-500)' }}>{fmtFull(row.impressions)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Creatives */}
          <div className="card" style={{ padding: '18px 20px' }}>
            <SectionLabel>Creatives</SectionLabel>
            {creativesLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 100, borderRadius: 6 }} />)}
              </div>
            ) : creatives.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--fg-faint)', padding: '8px 0' }}>No creatives found for this screen</div>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {creatives.map((c, i) => {
                  const isVideo = c.type === 'video' || /\.(mp4|mov|webm)$/i.test(c.creativeUrl ?? '');
                  const filename = c.creativeUrl ? decodeURIComponent(c.creativeUrl.split('?')[0].split('/').pop() ?? '') : '';
                  const displayName = c.name ?? filename ?? c.creativeUrl ?? '';
                  const goToCreative = () => {
                    if (!c.creativeUrl) return;
                    window.history.pushState(null, '', `/creatives?creative_url=${encodeURIComponent(c.creativeUrl)}`);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                  };
                  return (
                    <div key={c.creativeUrl ?? i}
                      onClick={goToCreative}
                      style={{
                        display: 'grid', gridTemplateColumns: '56px 1fr 90px 110px',
                        alignItems: 'center', gap: 12,
                        borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none',
                        cursor: c.creativeUrl ? 'pointer' : 'default',
                        transition: 'background 0.1s', margin: '0 -20px', padding: '10px 20px',
                      }}
                      onMouseEnter={e => { if (c.creativeUrl) e.currentTarget.style.background = 'var(--bg-surface-2)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {c.creativeUrl ? (
                        isVideo
                          ? <VideoThumb src={c.creativeUrl} />
                          : <img src={c.creativeUrl} alt="" style={{ width: 56, height: 100, borderRadius: 6, objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <div style={{ width: 56, height: 100, borderRadius: 6, background: 'var(--bg-surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-faint)', fontSize: 18 }}>
                          {isVideo ? '▶' : '◻'}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 700,
                          color: isVideo ? 'var(--brand-blue)' : '#FF5C00',
                          background: isVideo ? 'rgba(59,130,246,0.10)' : 'rgba(245,158,11,0.12)',
                          border: `1px solid ${isVideo ? 'rgba(59,130,246,0.20)' : 'rgba(245,158,11,0.25)'}`,
                          padding: '1px 6px', borderRadius: 4, display: 'inline-block', marginBottom: 4,
                        }}>
                          {isVideo ? '▶ Video' : '◻ Image'}
                        </span>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--fg-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
                      </div>
                      <div className="num" style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Plays</div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-default)' }}>{fmtFull(c.plays)}</span>
                      </div>
                      <div className="num" style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg-muted)', marginBottom: 2 }}>Impressions</div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--brand-green-500)' }}>{fmtFull(c.impressions)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>

        {/* Map */}
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--fg-strong)' }}>Location</div>
          </div>
          <div style={{ padding: 18 }}>
            <NetworkMap screens={[screen]} maxWidth={560} />
          </div>
        </div>
      </div>
    </div>
  );
}

export function ScreensPage({ campaigns: initialCampaigns, clientUuid }: { campaigns: Campaign[]; clientUuid: string }) {
  const [campaigns, setCampaigns] = useState<Campaign[]>(initialCampaigns);
  const [baseScreens, setBaseScreens] = useState<ScreenSummary[]>([]);
  const makeCustom = React.useCallback((c: { id: string; name: string; venue: string; lat: number; lon: number }) =>
    ({ ...c, plays: 0, impressions: 0, sensorEnabled: false } as ScreenSummary), []);
  const edits = useScreenEdits(baseScreens, makeCustom);
  const screens = edits.screens;
  const prefs = useMapPrefs();
  const [mapSize, setMapSize] = useState<number | null>(() => {
    try { const v = parseInt(window.localStorage.getItem('ss.mapSize') ?? '', 10); return v > 0 ? v : null; } catch { return null; }
  });
  const changeMapSize = (v: number | null) => {
    setMapSize(v);
    try { v ? window.localStorage.setItem('ss.mapSize', String(v)) : window.localStorage.removeItem('ss.mapSize'); } catch { /* ignore */ }
  };
  const [exporting, setExporting] = useState(false);
  const [redraw, setRedraw] = useState(false);
  const exportRef = React.useRef<HTMLDivElement>(null);
  const mapHandle = React.useRef<NetworkMapHandle>(null);
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const editTarget = screens.find(s => s.id === editId) ?? null;
  const [loading, setLoading] = useState(false);
  const [selectedScreen, setSelectedScreen] = useState<ScreenSummary | null>(null);
  const [startDate, setStartDate] = useState('');

  const campaignKey = campaigns.map(c => c.id).join(',');

  const getUrlScreenId = () => new URLSearchParams(window.location.search).get('screen_id');

  const openScreen = (s: ScreenSummary) => {
    setSelectedScreen(s);
    window.history.pushState(null, '', `/screens?screen_id=${encodeURIComponent(s.id)}`);
  };

  const closeScreen = () => {
    setSelectedScreen(null);
    window.history.pushState(null, '', '/screens');
  };

  useEffect(() => {
    const onPop = () => {
      const id = getUrlScreenId();
      if (!id) { setSelectedScreen(null); return; }
      setSelectedScreen(screens.find(s => s.id === id) ?? null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [screens]);

  useEffect(() => {
    if (initialCampaigns.length > 0) { setCampaigns(initialCampaigns); return; }
    if (!clientUuid) return;
    const fetch = clientUuid === '__reach__' ? getReachCampaigns() : getCampaigns(clientUuid);
    fetch.then(setCampaigns).catch(() => {});
  }, [clientUuid, initialCampaigns]);

  useEffect(() => {
    if (!campaigns.length) return;
    const sd = campaigns.reduce((min, c) => c.startDate < min ? c.startDate : min, campaigns[0].startDate);
    setStartDate(sd);
    setLoading(true);
    setBaseScreens([]);
    getAllScreens()
      .then(all => all.map(sc => ({ id: sc.id, name: sc.name, venue: sc.venue, lat: sc.lat, lon: sc.lon, plays: 0, impressions: 0, sensorEnabled: false } as ScreenSummary)))
      .then(results => {
        setBaseScreens(results);
        const id = getUrlScreenId();
        if (id) setSelectedScreen(results.find(s => s.id === id) ?? null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignKey]);

  // PNG export: a live Google map can't be rasterized, so render the same
  // pins (with edits applied) on the static tile map offscreen and capture it.
  const savePng = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Smartstreet_Screen-Locations.png';
    a.click();
  };

  // Preferred: Google Static Maps for the exact on-screen view. If that
  // fails (e.g. API not enabled), fall back to the static tile redraw.
  const exportPng = async () => {
    setExporting(true);
    try {
      const url = await mapHandle.current?.exportPng();
      if (url) { savePng(url); setExporting(false); return; }
    } catch (err) {
      console.error('Static Maps export failed, falling back', err);
    }
    setRedraw(true);
  };

  useEffect(() => {
    if (!redraw) return;
    let cancelled = false;
    (async () => {
      const el = exportRef.current;
      try {
        if (!el) return;
        const t0 = Date.now();
        await new Promise(r => setTimeout(r, 1200));
        while (Date.now() - t0 < 10000 && Array.from(el.querySelectorAll('img')).some(i => !i.complete)) {
          await new Promise(r => setTimeout(r, 200));
        }
        const canvas = await html2canvas(el, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
        if (!cancelled) savePng(canvas.toDataURL('image/png'));
      } catch { /* export failed; nothing to download */ }
      finally { if (!cancelled) { setExporting(false); setRedraw(false); } }
    })();
    return () => { cancelled = true; };
  }, [redraw]);

  const resetEverything = () => {
    if (!window.confirm('Reset all marker edits and label settings? This clears everything cached in this browser.')) return;
    edits.reset();
    prefs.resetAll();
    setEditId(null);
  };

  if (selectedScreen) {
    return (
      <ScreenDetailPage
        screen={selectedScreen}
        campaigns={campaigns}
        campaignKey={campaignKey}
        startDate={startDate}
        onBack={closeScreen}
      />
    );
  }

  return (
    <div>
      {redraw && (
        <div style={{ position: 'fixed', left: -10000, top: 0, width: 1000, pointerEvents: 'none' }}>
          <div ref={exportRef} style={{ width: 1000, height: 1000, background: '#fff' }}>
            <StaticNetworkMap screens={screens} width={1000} height={1000} dot={20} pad={60} controls={false} showNote={false} />
          </div>
        </div>
      )}
      <div className="card" style={{ padding: 0, marginBottom: 20, overflow: 'hidden' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>Smartstreet network — NYC</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>Live screen locations · sensor-verified</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={BTN} disabled={exporting || loading || !screens.length} onClick={exportPng}>
              {exporting ? 'Exporting…' : 'Export PNG'}
            </button>
            <button style={BTN} onClick={() => { setEditing(e => !e); setEditId(null); }}>
              {editing ? 'Done' : 'Edit locations'}
            </button>
          </div>
        </div>
        <div style={{ padding: 18 }}>
          {loading ? (
            <div style={{ height: 420, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>Loading screens…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', gap: 28 }}>
                <StatWidget label="Screens" value={screens.length} sub="In the network" dot="var(--brand-green-500)" />
                <StatWidget label="Locations" value={new Set(screens.map(s => `${s.lat.toFixed(4)},${s.lon.toFixed(4)}`)).size} sub="Distinct addresses" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--fg-muted)' }}>
                <span>Map size</span>
                <input
                  type="range" min={400} max={2400} step={20} style={{ flex: 1, maxWidth: 360 }}
                  value={mapSize ?? Math.max(400, Math.min(2400, window.innerHeight - 140))}
                  onChange={e => changeMapSize(+e.target.value)}
                />
                <span>{mapSize ? `${mapSize}px` : 'Fit'}</span>
                {mapSize != null && <button style={BTN} onClick={() => changeMapSize(null)}>Fit to screen</button>}
              </div>
              <div style={{ overflowX: 'auto' }}>
              <div style={{ width: mapSize ?? 'min(100%, calc(100vh - 140px))', margin: mapSize ? 0 : '0 auto' }}>
                <NetworkMap
                  ref={mapHandle}
                  screens={screens}
                  selectedId={editing ? editId : null}
                  onPinClick={editing ? setEditId : undefined}
                  onPinDrag={editing ? (id, lat, lon) => edits.update(id, { lat, lon }) : undefined}
                  labelPrefs={prefs.labels}
                  onMapClick={editing ? (lat, lon) => {
                    if (editId) edits.update(editId, { lat, lon });
                    else setEditId(edits.addCustom(lat, lon));
                  } : undefined}
                />
                {editing && (
                  <div style={{ marginTop: 12, padding: 12, border: '1px solid var(--border-soft)', borderRadius: 10, fontSize: 12 }}>
                    <div style={{ color: 'var(--fg-muted)', marginBottom: 8 }}>
                      {editTarget
                        ? 'Drag the marker (or click the map) to move it, or edit its details.'
                        : 'Click a marker to edit it, or click empty map to add a new one.'}
                    </div>
                    {editTarget && (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <input style={INPUT} value={editTarget.name} placeholder="Name" onChange={e => edits.update(editTarget.id, { name: e.target.value })} />
                        <input style={INPUT} value={editTarget.venue} placeholder="Address" onChange={e => edits.update(editTarget.id, { venue: e.target.value })} />
                        <div style={{ display: 'flex', gap: 8 }}>
                          <input style={{ ...INPUT, flex: 1 }} type="number" step="0.00001" value={editTarget.lat} onChange={e => edits.update(editTarget.id, { lat: parseFloat(e.target.value) || 0 })} />
                          <input style={{ ...INPUT, flex: 1 }} type="number" step="0.00001" value={editTarget.lon} onChange={e => edits.update(editTarget.id, { lon: parseFloat(e.target.value) || 0 })} />
                        </div>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button style={BTN} onClick={() => setEditId(null)}>Deselect</button>
                          {edits.isEdited(editTarget.id) && !editTarget.id.startsWith('custom-') && (
                            <button style={BTN} onClick={() => edits.reset(editTarget.id)}>Reset</button>
                          )}
                          <button style={BTN} onClick={() => { edits.remove(editTarget.id); setEditId(null); }}>Remove</button>
                        </div>
                      </div>
                    )}
                    <div style={{ borderTop: '1px solid var(--border-soft)', marginTop: 10, paddingTop: 10 }}>
                      <div style={{ fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 6 }}>Labels &amp; landmarks</div>
                      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 8 }}>
                        {([['streets', 'Street names'], ['landmarks', 'Landmarks'], ['state', 'State names']] as const).map(([k, l]) => (
                          <label key={k} style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                            <input type="checkbox" checked={prefs.labels[k]} onChange={e => prefs.setLabel(k, e.target.checked)} /> {l}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                      <button style={BTN} onClick={() => setEditId(null)}>+ New marker</button>
                      <button style={BTN} onClick={resetEverything}>Reset all changes</button>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading ? null : screens.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.3 }}>◯</div>
          <div style={{ fontWeight: 700, color: 'var(--fg-strong)', marginBottom: 4 }}>No screens found</div>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{campaigns.length === 0 ? 'No campaigns loaded.' : 'No screens returned for these campaigns.'}</div>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-strong)' }}>
              All screens <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 400, marginLeft: 4 }}>{screens.length}</span>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-surface-2)' }}>
                {['Screen', 'Address', 'Screen ID'].map((h, i) => (
                  <th key={h} style={{ textAlign: i === 2 ? 'right' : 'left', padding: '10px 18px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--fg-muted)', borderBottom: '1px solid var(--border-subtle)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {screens.map((s, idx) => (
                <tr
                  key={s.id}
                  onClick={() => openScreen(s)}
                  style={{ borderBottom: idx < screens.length - 1 ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer', transition: 'background 0.1s' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                >
                  <td style={{ padding: '12px 18px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg-strong)' }}>{s.name}</div>
                  </td>
                  <td style={{ padding: '12px 18px', fontSize: 12.5, color: 'var(--fg-muted)' }}>{s.venue || '—'}</td>
                  <td style={{ padding: '12px 18px', textAlign: 'right', fontSize: 12, color: 'var(--fg-muted)' }} className="num">{s.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
